<template>
  <div class="sidebar-navigation-menu bg-gray-200 border-r border-gray-300 overflow-hidden">
    <ElMenu
      :collapse="isCollapsed"
      default-active="dashboard"
      :default-openeds="openedSubmenus"
      class="navigation-menu w-full bg-gray-200 border-none"
    >
      <ElMenuItem
        v-for="(item, index) in visibleMenuItems"
        :key="index"
        :index="index.toString()"
        class="select-none"
        :class="['navigation-item', item.itemClass]"
        @click="onMenuItemPress(item.onClickHandling)"
      >
        <RouterLink
          :to="{ name: item.route.name, params: item.route.params }"
          tag="div"
          :class="[{ 'back-to-dashboard': !isRootView },
          { 'no-highlight': selectedFilterIndexes.length !== 0 }, 'navigation-link']"
        >
          <FontAwesomeIcon
            fixed-width
            :icon="item.iconClasses"
            class="text-xl mr-20"
          />
          <span class="menu-item-title truncate">
            <span class="truncate">
              {{ item.itemLabel }}
            </span>
            <span v-if="item.itemLabelCount">
              {{ item.itemLabelCount }}
            </span>
          </span>
        </RouterLink>
      </ElMenuItem>

      <template v-for="(submenu, submenuIndex) in generatedFilterSubmenus">
        <ElSubmenu
          v-if="generatedFilterSubmenus.length"
          :index="'submenu-index-' + submenuIndex"
          :key="'submenu-key-' + submenuIndex"
        >
          <template slot="title">
            <FontAwesomeIcon
              fixed-width
              :icon="submenu.iconClasses"
              class="text-xl mr-20"
            />

            <span class="menu-item-title select-none">{{ submenu.title }}</span>
          </template>
          <ElMenuItem
            v-for="item in submenu.items"
            :key="item.itemLabel"
            :index="item.itemLabel"
            :class="filterClass(item)"
            @click="onFilterPress(item.filterIndex)"
          >
            <template v-if="item.iconClassNames">
              <FontAwesomeIcon
                fixed-width
                :icon="item.iconClassNames"
                class="text-xl mr-20"
              />
              <span class="menu-item-title select-none">{{ item.itemLabel }}</span>
            </template>
            <span
              v-else
              class="sub-menu-item-title select-none"
              :title="item.itemLabelCountTitle"
              @mouseover="onMouseOverFilter(item.filterIndex)"
              @mouseout="onMouseOutFilter"
            >
              <span
                :title="filterLabelTitle(filterIsSelected(item.filterIndex), item.itemLabel)"
                class="item-label truncate"
              >
                {{ item.itemLabel }}
              </span>
              <span :title="item.itemLabelCountTitle">
                <FontAwesomeIcon
                  v-if="hoverFilterIndex === item.filterIndex && filterIsSelected(item.filterIndex)"
                  class="remove-filter-icon"
                  :icon="['far', 'times']"
                />
                <span v-else>
                  {{ item.itemLabelCount }}
                </span>
              </span>
            </span>
          </ElMenuItem>
        </ElSubmenu>
      </template>

      <ElMenuItem
        v-if="['EventUpdate', 'EventDetails'].includes($route.name) && currentEvent
        && currentEvent.ownerId === userAuthDataId"
        key="event-settings"
        index="event-settings"
      >
        <RouterLink
          :to="{ name: 'EventUpdate', params: { eventID: currentEvent.id } }"
          tag="div"
          class="navigation-link"
        >
          <FontAwesomeIcon
            fixed-width
            :icon="['fad', 'cogs']"
            class="text-xl mr-20"
          />
          <span class="menu-item-title">
            Settings
          </span>
        </RouterLink>
      </ElMenuItem>

      <ElMenuItem
        v-if="['EventUpgrade'].includes($route.name) && currentEvent"
        key="event-upgrade"
        index="event-upgrade"
      >
        <RouterLink
          :to="{ name: 'EventUpgrade', params: { eventID: currentEvent.id } }"
          tag="div"
          class="navigation-link"
        >
          <FontAwesomeIcon
            fixed-width
            :icon="['fad', 'angle-double-up']"
            class="text-xl mr-20"
          />
          <span class="menu-item-title">
            Upgrade
          </span>
        </RouterLink>
      </ElMenuItem>

      <ElMenuItem
        v-if="['Dashboard', 'TeamManagement'].includes($route.name)"
        key="event-upgrade"
        index="event-upgrade"
      >
        <RouterLink
          :to="{ name: 'TeamManagement' }"
          tag="div"
          class="navigation-link"
        >
          <FontAwesomeIcon
            fixed-width
            :icon="['fad', 'user-friends']"
            class="text-xl mr-20"
          />
          <span class="menu-item-title">
            Team
<!--            <span class="font-medium text-sm text-primary-light tracking-widest">-->
<!--              BETA-->
<!--            </span>-->
          </span>
        </RouterLink>
      </ElMenuItem>

      <ElMenuItem
        v-if="['SubscriptionPlans'].includes($route.name)"
        key="event-upgrade"
        index="event-upgrade"
      >
        <RouterLink
          :to="{ name: 'SubscriptionPlans' }"
          tag="div"
          class="navigation-link"
        >
          <FontAwesomeIcon
            fixed-width
            :icon="['fad', 'crown']"
            class="text-xl mr-20"
          />
          <span class="menu-item-title">
            Subscribe
          </span>
        </RouterLink>
      </ElMenuItem>
    </ElMenu>

    <!-- Event upgrade -->
    <div
      v-if="$route.name === 'EventDetails' && currentEvent && currentEvent.status === 0
      && currentEvent.packageId !== highestOneOffPackageId && currentEvent.ownerId === userAuthDataId"
      class="p-20 bg-gray-100 border-t border-gray-300 shadow z-10"
    >
      <p class="font-bold text-xl text-primary leading-tight mb-10">
        Add more guests
      </p>
      <p class="text-base leading-tight mb-20">
       Upgrade to add more guests & devices
      </p>
      <RouterLink
        :to="{ name: 'EventUpgrade', params: { eventID: currentEvent.id } }"
        class="flex items-center justify-between text-white bg-primary shadow-3dp transition-shadow
        duration-200 ease-in-out-quart rounded-5 overflow-hidden hover:shadow-8dp"
        @click.stop
      >
        <span class="w-full text-base text-center leading-none">
          Upgrade
        </span>
        <span class="py-10 px-20 bg-primary-dark">
          <FontAwesomeIcon
            :icon="['far', 'angle-double-up']"
            class="text-2xl text-white"
          />
        </span>
      </RouterLink>
    </div>
    <!-- Become subscriber -->
    <div
      v-else-if="$route.name !== 'EventDetails' && $route.name !== 'EventUpgrade'
      && $route.name !== 'SubscriptionPlans' && !activeSubscriptionPlanPackage && !userPackageIsInitialLoading"
      class="p-20 bg-gray-100 border-t border-gray-300 shadow-6dp z-10"
    >
      <p class="font-bold text-xl text-primary leading-tight mb-10">
        Subscribe & Save
      </p>
      <p class="text-base leading-tight mb-20">
        Create as many events as you want
      </p>
      <RouterLink
        :to="{ name: 'SubscriptionPlans' }"
        class="flex items-center justify-between text-white bg-primary shadow-3dp transition-shadow duration-200
        ease-in-out-quart rounded-5 overflow-hidden hover:shadow-8dp"
        @click.stop
      >
        <span class="w-full text-base text-center leading-none">
          Subscribe
        </span>
        <span class="py-12 px-15 bg-primary-dark">
          <FontAwesomeIcon
            :icon="['fas', 'crown']"
            class="text-lg text-white"
          />
        </span>
      </RouterLink>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { guestStatus } from '@/utils/utilityFunctions';
import guestPluralRules from '@/utils/pluralRules/guest';
import extraGuestPluralRules from '@/utils/pluralRules/extraGuest';

export default {
  data: () => ({
    openedSubmenus: [], // By default open all submenus
    isCollapsed: false,
    hoverFilterIndex: -1,
  }),
  computed: {
    ...mapState('user/events', {
      allEvents: 'all',
    }),
    ...mapState('user/package', {
      userPackageIsInitialLoading: 'isInitialLoading',
    }),
    ...mapGetters('packages', {
      highestOneOffPackageId: 'highestOneOffPackageId',
    }),
    ...mapGetters('user/auth', {
      userAuthDataId: 'authDataId',
    }),
    ...mapGetters('user/package', {
      activeSubscriptionPlanPackage: 'activeSubscriptionPlanPackage',
    }),
    currentEvent() {
      return this.allEvents[this.$route.params.eventID];
    },
    guestFilterCounts() {
      return this.$store.state?.guest?.filter?.filterCounts;
    },
    selectedFilterIndexes() {
      if (!this.$store.state?.guest?.filter?.selectedFilterIndexes) return [];
      return this.$store.state.guest.filter.selectedFilterIndexes;
    },
    isRootView() {
      return this.$route.name === 'Dashboard' || this.$route.name === 'TeamManagement';
    },
    menuItems() {
      const totalGuests = this.$store.state?.guest?.guestsCounts?.mainGuest
      + this.$store.state?.guest?.guestsCounts?.extraGuest;

      return [{
        route: {
          name: 'Dashboard',
        },
        itemClass: {
          'back-to-previous-page': !this.isRootView,
        },
        iconClasses: this.isRootView ? ['fad', 'calendar-day'] : ['fas', 'arrow-left'],
        isVisible: true,
        itemLabel: 'Events',
      }, {
        route: {
          name: 'EventCreate',
        },
        iconClasses: ['far', 'plus'],
        isVisible: ['EventCreate'].includes(this.$route.name),
        itemLabel: 'Create',
      }, {
        route: {
          name: 'EventDetails',
          params: {
            eventID: this.$route.params.eventID,
          },
        },
        iconClasses: ['fad', 'users'],
        isVisible: ['EventDetails'].includes(this.$route.name),
        itemLabel: 'All guests',
        itemLabelCount: ((Number.isNaN(totalGuests) || !totalGuests) ? '-' : totalGuests).toLocaleString(),
        onClickHandling: this.resetAllFilters,
      }, {
        route: {
          name: 'UserProfile',
        },
        iconClasses: ['fad', 'user-cog'],
        isVisible: this.$route.name === 'UserProfile',
        itemLabel: 'Profile',
      }];
    },
    generatedFilterSubmenuItems() {
      if (!this.$store.state?.guest?.filter?.filters) return [];
      // `${guestPluralRules(this.guestFilterCounts[filterIndex].mainGuest.toLocaleString())} ${this.guestFilterCounts[filterIndex].extraGuest ? `(+${extraGuestPluralRules(this.guestFilterCounts[filterIndex].extraGuest.toLocaleString())})` : ''}`;
      let itemLabelCountTitle = null;
      this.$store.state?.guest.filter.filters.map((filter, filterIndex) => {
        itemLabelCountTitle = guestPluralRules(this.guestFilterCounts[filterIndex].mainGuest.toLocaleString());
        if (this.guestFilterCounts[filterIndex].extraGuest) {
          itemLabelCountTitle += extraGuestPluralRules(this.guestFilterCounts[filterIndex].extraGuest.toLocaleString());
        }
        return false;
      });

      return this.$store.state?.guest.filter.filters.map((filter, filterIndex) => ({
        itemLabel: filter.name,
        itemLabelCount: (this.guestFilterCounts[filterIndex].mainGuest + this.guestFilterCounts[filterIndex].extraGuest)
          .toLocaleString(),
        itemLabelCountTitle,
        groupIndex: filter.groupIndex,
        className: filter.groupIndex === 0 ? guestStatus[filterIndex].navigationClasses : 'list-item',
        filterIndex,
      }));
    },
    generatedFilterSubmenus() {
      const submenuItems = [];
      if (!this.$store.state?.guest?.filter?.filterGroups) return submenuItems;

      if (this.$route.name === 'EventDetails' && this.$route.params.eventID) {
        this.$store.state.guest.filter.filterGroups.forEach((group, groupIndex) => {
          submenuItems.push({
            title: group.name,
            iconClasses: group.iconClasses,
            items: this.generatedFilterSubmenuItems.filter((submenuItem) => submenuItem.groupIndex === groupIndex),
          });
        });
      }

      return submenuItems;
    },
    visibleMenuItems() {
      return this.menuItems.filter((menuItem) => menuItem.isVisible);
    },
  },
  methods: {
    ...mapActions('guest/filter', {
      resetSelectedFilterIndexes: 'resetSelectedFilterIndexes',
      updateSelectedFilterIndexes: 'updateSelectedFilterIndexes',
    }),
    resetAllFilters() {
      this.resetSelectedFilterIndexes();
    },
    filterLabelTitle(isSelected, filterName) {
      if (isSelected) return `Press to remove the '${filterName}' filter`;
      return `Press to see '${filterName}' guests`;
    },
    filterClass(menuItem) {
      if (this.filterIsSelected(menuItem.filterIndex)) return [menuItem.className, 'is-selected'];
      return '';
    },
    filterIsSelected(filterIndex) {
      return this.selectedFilterIndexes
        .findIndex((selectedFilterIndex) => selectedFilterIndex === filterIndex) !== -1;
    },
    onMenuItemPress(onClickHandling) {
      if (onClickHandling) onClickHandling();
    },
    onFilterPress(filterIndex) {
      window.scrollTo({ x: 0, y: 0 });
      this.updateSelectedFilterIndexes(filterIndex);
    },
    onMouseOverFilter(filterIndex) {
      this.hoverFilterIndex = filterIndex;
    },
    onMouseOutFilter() {
      this.hoverFilterIndex = -1;
    },
  },
};
</script>

<style scoped>
.navigation-link {
  @apply h-full flex items-center font-medium;
  /*@apply py-0 pr-25 !important;*/

  color: #54595f;
  padding: 0 20px 0 25px;
  /*padding-left: max(20px, env(safe-area-inset-left));;*/

  &.router-link-exact-active:not(.no-highlight) {
    @apply text-secondary bg-gray-300;
  }
}

.link-active {
  @apply font-medium;
}

.sidebar-navigation-menu {
  @apply relative w-auto h-full flex flex-col;

  .navigation-menu {
    flex: 1 1 auto;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;

    .navigation-item {
      @apply border-b border-gray-300;

      &.back-to-previous-page {
        @apply border-b border-gray-400;
      }
    }

    li {
      @apply p-0 !important;
      @apply w-full m-0;

      &.el-menu-item:hover {
        @apply bg-gray-300 !important;
      }

      &.el-menu-item:focus {
        @apply bg-gray-100 !important;
      }
    }

    .el-submenu {
      &.is-opened {
        @apply border-gray-300;

        >>> .el-submenu__title {
          @apply border-gray-300;
        }
      }

      >>> .el-menu {
        @apply border-l-0 border-t-0 border-r-0 border-b border-solid border-gray-300;
      }

      .el-menu-item {
        @apply pl-30 pr-20 !important;
        @apply bg-gray-100;

        border-left: 4px solid transparent;

        &.is-selected {
          @apply bg-gray-300 !important;
        }

        &.status-none {
          @apply text-status-none border-status-none;
        }

        &.status-attending {
          @apply text-status-attending border-status-attending;
        }

        &.status-checked-in {
          @apply text-status-checked-in border-status-checked-in;
        }

        &.status-checked-out {
          @apply text-status-checked-out border-status-checked-out;
        }

        &.status-invited {
          @apply text-status-invited border-status-invited;
        }

        &.status-declined {
          @apply text-status-declined border-status-declined;
        }

        &.list-item {
          @apply text-secondary border-secondary;
        }
      }

      >>> .el-submenu__icon-arrow {
        @apply font-bold text-base;
      }

      >>> .el-submenu__title,
      .el-menu-item {
        @apply font-medium text-primary-light;
      }

      >>> .el-submenu__title {
        @apply px-25 !important;
        @apply border-b border-transparent;
      }

      >>> .el-submenu__title:hover,
      .el-menu-item:hover {
        @apply bg-gray-300 !important;
      }
    }

    .router-link-exact-active .sub-menu-item-title {
      @apply text-secondary;
    }

    .router-link-container {
      @apply h-full;
    }

    .menu-item-title, .sub-menu-item-title {
      font-size: 1rem;
      line-height: normal;

      i {
        color: rgba(84, 89, 95, 1);
      }
    }

    .menu-item-title {
      @apply w-full inline-flex justify-between;
    }

    .sub-menu-item-title {
      @apply h-full flex items-center justify-between;

      font-size: 0.95rem;

      .item-label {
        @apply mr-10;
      }

      .remove-filter-icon {
        @apply text-secondary;

        font-size: 1.1rem;
      }
    }

    .status-none .remove-filter-icon {
      @apply text-status-none;
    }

    .status-invited .remove-filter-icon {
      @apply text-status-invited;
    }

    .status-attending .remove-filter-icon {
      @apply text-status-attending;
    }

    .status-checked-in .remove-filter-icon {
      @apply text-status-checked-in;
    }

    .status-checked-out .remove-filter-icon {
      @apply text-status-checked-out;
    }

    .status-declined .remove-filter-icon {
      @apply text-status-declined;
    }
  }
}
</style>
